<template>
  <main-layout>
    <base-breadcrumb :routes="breadcrumb" />
    <appointment-invitations-listable-manager
      v-if="isAppointment"
      ref="listable"
      :kwargs="kwargs"
      :search-term="searchTerm"
      @input="onItemsUpdated"
    />
    <customer-outreachs-listable-manager
      v-else
      ref="listable"
      :kwargs="kwargs"
      :search-term="searchTerm"
      @input="onItemsUpdated"
    />
    <base-card class="p-2">
      <div class="d-flex justify-content-between align-items-center mb-3 mt-n1">
        <base-heading
          :level="2"
          class="ml-n1"
        >
          Activity
        </base-heading>
        <div
          v-if="!customer"
          class="d-flex align-items-center"
        >
          <input
            v-model="searchTerm"
            type="text"
            class="activity-search-input form-control pr-5"
            placeholder="Search"
          >
          <base-icon
            icon="search"
            class="activity-search-icon"
            width="17px"
          />
        </div>
      </div>
      <div class="d-flex title headers mb-n2">
        <div class="col-3">
          CLIENT
        </div>
        <div class="ml-2 col-6">
          CLIENT EMAIL
        </div>
        <div class="col-1 text-center">
          <!-- STATUS -->
        </div>
        <div class="col-1 text-center">
          {{ isAppointment ? 'LIKES' : 'OPENS' }}
        </div>
        <div class="col-1 text-center">
          CLICKS
        </div>
      </div>
      <base-list
        :items="items"
        :loading="outLoading"
        mode="loadMore"
        :show-load-more="true"
        :count="count"
        @reached-bottom="onBottomReached"
      >
        <template #loading>
          <base-skeleton
            v-for="n in 4"
            :key="`outr_activity_skeleton__${n}`"
            :opacity="0 + (1 /n)"
          >
            <div class="d-flex row m-0">
              <div class="w-100 d-flex align-items-end">
                <div
                  class="skeleton__item col-3 mr-1"
                />
                <div
                  class="skeleton__item col-7 mr-1"
                />
                <div
                  class="skeleton__item col-1 mr-1"
                />
                <div
                  class="skeleton__item col-1 mr-n2"
                />
              </div>
            </div>
          </base-skeleton>
        </template>
        <template
          v-if="isAppointment"
          #default="{item: {appointment, pk: invitationId, customer: client, anon, status, clicks, likes}}"
        >
          <base-cell class="semi-black py-3">
            <base-text
              size="m"
              class="col-3"
              :class="{clickable: client}"
              @click="anon ? () => {} : onCustomerClicked(client.pk)"
            >
              {{ client ? `${client.display_name_array[0]}` : 'Anonymous' }}
            </base-text>
            <div class="col-7 d-flex justify-content-between">
              <base-text
                size="md"
                :class="{clickable: client}"
                @click="anon ? () => {} : onCustomerClicked(client.pk)"
              >
                {{ client ? client.email : '--' }}
              </base-text>
            </div>
            <div
              class="text-center px-0 class col-2 row no-gutters clickable underline"
              @click="viewInvitationEventDetails(client, anon, invitationId)"
            >
              <base-text
                size="md"
                class="pl-3 col-6 text-center black"
              >
                {{ likes }}
              </base-text>
              <base-text
                size="md"
                class="pl-3 col-6 text-center black"
              >
                {{ clicks }}
              </base-text>
            </div>
          </base-cell>
        </template>
        <template
          v-else
          #default="{item: {customer: client, opens, clicks, unsubscribes, bounces, outreach: {edit}}}"
        >
          <base-cell class="semi-black py-3">
            <base-text
              size="m"
              class="col-3 clickable"
              @click="onCustomerClicked(client.pk)"
            >
              {{ client.display_name_array[0] }}
            </base-text>
            <div class="col-7 d-flex justify-content-between">
              <base-text
                size="md"
                class="clickable"
                @click="onCustomerClicked(client.pk)"
              >
                {{ client.email }}
              </base-text>
              <div
                class="d-flex clickable"
                @click="viewEditEventDetails(client, edit)"
              >
                <div
                  v-if="bounces"
                  class="status-badge status-badge--bounced"
                >
                  Bounced
                </div>
                <div
                  v-if="unsubscribes"
                  class="status-badge status-badge--unsubscribed"
                >
                  Unsubscribed
                </div>
              </div>
            </div>
            <div
              class="text-center px-0 class col-2 row no-gutters clickable underline"
              @click="viewEditEventDetails(client, edit)"
            >
              <base-text
                size="md"
                class="pl-3 col-6 text-center black"
              >
                {{ opens }}
              </base-text>
              <base-text
                size="md"
                class="pl-3 col-6 text-center black"
              >
                {{ clicks }}
              </base-text>
            </div>
          </base-cell>
        </template>
      </base-list>
    </base-card>
    <client-email-events-container
      v-if="showEventsModal"
      :show="showEventsModal"
      :customer="modalCustomer"
      :subject="modalSubject"
      :date="modalDate"
      @closed="onEventsModalClosed"
    />
  </main-layout>
</template>
<script type="text/javascript">

import ClientEmailEventsContainer from '@/components/Widgets/ClientHistory/ClientEmailEventsContainer';
import { EMAIL_EVENTS, APPOINTMENT_EVENTS } from '@/_utilities/Enums';

export default {
  name: 'ReportActivity',
  components: { ClientEmailEventsContainer },
  props: {
    customer: {
      type: String,
      default: null, // customerEmail
    },
  },
  data() {
    return {
      showEventsModal: false,
      outLoading: false,
      modalCustomer: '',
      modalSubject: '',
      modalDate: null,
      searchTerm: '',

      items: [],
      count: 0,
    };
  },
  computed: {
    kwargs() {
      if (this.isAppointment) {
        return {
          appointment: this.appointmentId,
          event: Object.prototype.hasOwnProperty.call(APPOINTMENT_EVENTS, this.event) ? this.event : null,
        };
      } else {
        return {
          group: this.$route.query.group,
          outreach: this.outreachId,
          event: Object.prototype.hasOwnProperty.call(EMAIL_EVENTS, this.event) ? this.event : null,
          customer_id: this.$route.params.customerId,
        };
      }
    },
    isAppointment() {
      return this.$route.name === 'AppointmentActivity';
    },
    appointmentId() {
      return this.$route.params.appointmentId;
    },
    appointment() {
      return this.$store.state.appointments.item;
    },
    outreachId() {
      if (this.isAppointment) return null;
      const { id, outreach } = this.$route.params;
      return id || outreach;
    },
    event() {
      return this.$route.params.event;
    },
    customerId() {
      return this.$route.params.customerId;
    },
    breadcrumb() {
      // Get customer info. fetchCustomer called on mount is responsible of fetching and populating this.
      const customer = this.$store.state.customers.item;

      const result = [];
      // Base
      if (this.customerId) {
        result.push(...[{
          name: 'Clients',
          link: '/clients',
        }, {
          name: `${customer}`,
          link: `/clients/${this.customerId}`,
        }]);
      } else {
        result.push({
          name: 'Reports',
          link: '/reports',
        });
      }

      // Outreach || Appointment
      result.push({
        name: this.title,
        link: `${this.customerId ? `/clients/${this.customerId}` : '/reports'}/${this.isAppointment
          ? `appointment/${this.appointmentId}`
          : `outreach/${this.outreachId}`}/stats`,
      });

      // Event filter if any
      if (this.event) {
        if (!this.customerId) {
          result.push({
            name: this.niceTitle(this.event),
            // link: `/reports/outreach/${this.outreach}/activity/${this.event}`,
          });
        }
      }

      // Group filter if any
      if (this.group) {
        result.push({
          name: this.group.name,
        });
      }

      return result;
    },
    loading() {
      return this.isAppointment ? this.$store.state.appointments.loading
        : this.$store.state.outreachs.corcsLoading;
    },
    title() {
      const editTitle = this.isAppointment
        ? (this.$store.state.appointments.item.subject ?? 'Appointment Stats')
        : (this.$store.state.outreachs?.stats?.edit?.subject ?? 'Outreach Stats');
      return this.$options.filters.striphtml(editTitle);
    },
    group() {
      if (this.$route.query.group === undefined) return null;
      if (this.$route.query.group === 0) {
        return { name: 'Clients' };
      }
      const { group } = this.$store.state.groups;
      if (Object.keys(group).length === 0 && group.constructor === Object) { // object is empty
        return null;
      }
      return group;
    },
  },
  mounted() {
    // Fetch appoitment data if needed
    if (this.isAppointment
      && `${this.$store.state.appointments.item.pk}` !== `${this.appointmentId}`) this.$store.dispatch('appointments/getReportItem', this.appointmentId);

    // Fetch outreach activity data if needed
    if (!this.isAppointment) {
      this.$store.dispatch('outreachs/fetchStats', { id: this.outreachId, customerId: this.customerId });
      if (this.$route.query.group) this.$store.dispatch('groups/fetchGroup', this.$route.query.group);
    }

    // if we're on customer specific page, fetch customer data first
    if (this.customerId) {
      this.$store.dispatch('customers/fetchItem', {
        id: this.customerId,
        scope: this.$store.getters['account/scope'],
      });
    }
    if (this.loading) {
      this.outLoading = this.loading;
    } else {
      this.outLoading = true;
    }
    setTimeout(() => {
      this.outLoading = false;
    }, 550);
  },
  methods: {
    onItemsUpdated(evt) {
      const { count, items } = evt;
      console.log('ITEMS UPDATED:', items);
      this.count = count;
      this.items = items;
    },
    niceTitle(event) {
      if (Object.prototype.hasOwnProperty.call(EMAIL_EVENTS, event)) {
        return EMAIL_EVENTS[event];
      }
      return event.split('_').map((word) => `${word[0].toUpperCase()}${word.slice(1).toLowerCase()}`).join(' ');
    },
    viewEditEventDetails(customer, edit) {
      if (this.isAppointment) return;
      this.modalSubject = this.$options.filters.striphtml(edit?.subject);
      this.modalDate = edit?.sent_date;
      this.modalCustomer = customer;
      this.showEventsModal = true;
    },
    viewInvitationEventDetails(customer, anon, invitationId) {
      if (!this.isAppointment) return;
      // prepare modal data
      this.modalSubject = this.$options.filters.striphtml(this.appointment.subject);
      this.modalDate = this.appointment.start_date;
      this.modalCustomer = customer || {
        first_name: 'Anonymous',
        last_name: '',
        email: '--',
        anon,
      };
      this.modalCustomer.invitationId = invitationId;
      // show
      this.showEventsModal = true;
    },
    onEventsModalClosed() {
      this.showEventsModal = false;
      this.modalSubject = '';
      this.modalDate = null;
      this.modalCustomer = null;
    },
    onCustomerClicked(customerPk) {
      this.$router.push(`/clients/${customerPk}`);
    },
    onBottomReached() {
      this.$nextTick().then(() => {
        this.$refs.listable.loadMore();
      });
    },
  },
};
</script>
<style lang="scss"
  scoped>
.status-badge {
  background: #D98653;
  color: white;
  font-size: 10px;
  padding: 3px 6px 2px 6px;
  border-radius: 3px;
  margin: 0px 3px;
  height: min-content;

  &--delivered {
    background-color: $dusty-rose;
  }

  &--open {
    background-color: $orange;
  }

  &--bounced {
    background-color: $dusty-rose;
  }

  &--unsubscribed {
    background-color: $orange;
  }
}

.status-cell {
  min-width: 50px;
}

.headers {
  color: #AAA;
  font-size: 11px;
}

.semi-black {
  color: $black;
}

.black {
  color: #000;
}

.clickable {
  cursor: pointer;
  &.underline:hover {
    text-decoration: underline;
  }
}

::v-deep .skeleton__container {
  padding-right: 1vw;
}

.activity-search-input {
  border-color: transparent;
  font-size: 0.75rem;
  background: $gray-light-3;
}

.activity-search-icon {
  margin-left: -1.7rem !important;
}
</style>
